import { Position, useReactFlow, useUpdateNodeInternals } from '@xyflow/react';
import { useState, useEffect, useRef } from 'react';
import { useTrashNode } from './commons/trashNode';
import { FlowHandle } from './commons/flowHandle';
import './style.scss';
import { variableTypes } from 'services/dropdown-enums';
import { UilTrashAlt, UilPen, UilPlus } from '@iconscout/react-unicons';
import SimpleLabelTextBox from 'pub-components/ui-controls/ui-components/SimpleLabelTextbox';
import { useEdges } from '@xyflow/react';
import ModalPopup from 'features/settings/pvt-components/chatbot-settings/modal-popup';
import DropDownEditable from 'pub-components/ui-controls/DropDownEditable';
import { useFormik } from 'formik';
import { extractVariableNodeSchema } from 'services/schemas/chatbotSchemas/schemas';
import { extractVariableNodeDefaultVariableValues } from 'services/schemas/chatbotSchemas';
import { Button } from 'react-bootstrap';
import { handleClipboardKeyDown } from '../helper';

// Ensure every variable has an id
function ensureVariableIds(variables, nodeId) {
  if (!Array.isArray(variables)) return [];
  return variables.map((v, i) => ({
    ...v,
    id:
      v.id && String(v.id).startsWith(`variable-${nodeId}-`)
        ? v.id
        : `variable-${nodeId}-${i}`
  }));
}

export default function ExtractVariableNode({ id, data, editable = true }) {
  const { setNodes, setEdges } = useReactFlow();
  const trashNode = useTrashNode();
  const edges = useEdges();

  const DEFAULT_TRANSITIONS = [
    { id: `transition-${id}-0`, value: 'Success' },
    { id: `transition-${id}-1`, value: 'Failure' }
  ];

  const [showModal, setShowModal] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const updateNodeInternals = useUpdateNodeInternals();

  const [variables, setVariables] = useState(() =>
    ensureVariableIds(data?.variables || [], id)
  );
  const [transitions] = useState(
    data?.transitions?.length ? data.transitions : DEFAULT_TRANSITIONS
  );

  // Variable type options for dropdown - generated from enum
  const variableTypeOptions = Object.keys(variableTypes).map(key => ({
    name: key,
    value: variableTypes[key],
    id: variableTypes[key]
  }));

  useEffect(() => {
    updateNodeInternals(id);
  }, []);

  const onChange = newData => {
    setNodes(nds =>
      nds.map(node =>
        node.id === id ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
  };

  // 🔹 Add variable
  const addVariable = () => {
    setVariables(prev => {
      const maxIndex = prev.reduce((max, variable) => {
        const match = variable.id?.match?.(
          new RegExp(`^variable-${id}-(\\d+)$`)
        );
        if (!match) return max;
        const num = parseInt(match[1], 10);
        return Number.isNaN(num) ? max : Math.max(max, num);
      }, -1);

      const nextIndex = maxIndex + 1;

      const newVariable = {
        id: `variable-${id}-${nextIndex}`,
        name: '',
        description: '',
        type: null
      };

      const updatedVariables = [...prev, newVariable];
      onChange({ variables: updatedVariables });
      return updatedVariables;
    });
  };

  // 🔹 Update variable
  const updateVariable = (variableId, data) => {
    const updatedVariables = variables.map(variable =>
      variable.id === variableId ? { ...variable, ...data } : variable
    );
    setVariables(updatedVariables);
    onChange({ variables: updatedVariables });
  };

  // 🔹 Save variable from modal
  const saveVariableFromModal = values => {
    updateVariable(selectedVariable.id, values);
    closeVariableModal();
  };

  // 🔹 Initialize formik for modal
  const modalFormik = useFormik({
    enableReinitialize: true,
    initialValues: extractVariableNodeDefaultVariableValues,
    validationSchema: extractVariableNodeSchema,
    onSubmit: saveVariableFromModal
  });

  // 🔹 Open variable edit modal
  const openVariableModal = variable => {
    setSelectedVariable(variable);
    // Populate Formik with variable data like chatTransfer pattern
    modalFormik.resetForm({
      values: {
        name: variable?.name || '',
        description: variable?.description || '',
        type: variable?.type || null
      }
    });
    setShowModal(true);
  };

  // 🔹 Close variable edit modal
  const closeVariableModal = () => {
    setSelectedVariable(null);
    setShowModal(false);
  };

  // 🔹 Remove variable
  const removeVariable = variableId => {
    const updatedVariables = variables.filter(
      variable => variable.id !== variableId
    );
    setVariables(updatedVariables);
    onChange({ variables: updatedVariables });
  };

  const IsValidConnection = connection => {
    // limit 1 connection per source handle
    if (edges.some(edge => edge.sourceHandle === connection.sourceHandle))
      return false;

    return true;
  };

  // When loaded/restored data has variables without ids, normalize variables array with IDs
  const didNormalizeVariablesRef = useRef(false);
  useEffect(() => {
    if (didNormalizeVariablesRef.current) return;
    const raw = data?.variables;
    if (!Array.isArray(raw) || raw.length === 0) return;
    const hasMissingId = raw.some(
      v => !v.id || !String(v.id).startsWith(`variable-${id}-`)
    );
    if (!hasMissingId) return;
    didNormalizeVariablesRef.current = true;
    const normalized = ensureVariableIds(raw, id);
    setVariables(normalized);
    onChange({ variables: normalized });
  }, [id, data?.variables]);

  // 🔹 Persist data on first mount
  useEffect(() => {
    if (!data?.transitions || data.transitions.length === 0) {
      onChange({
        variables: variables.length === 0 ? addVariable() : variables,
        transitions
      });
    }
  }, []);

  return (
    <dl className="mt-3" style={{ width: '290px' }}>
      {/* Left (incoming) handle */}
      <FlowHandle
        type="target"
        position={Position.Left}
        id={`${id}-target`}
        editable={editable}
        isConnectable={true}
      />
      <div
        className="card shadow-sm border-gray rounded-10"
        style={{ backgroundColor: '#F9F9F9' }}
      >
        <div
          className="d-flex align-items-center px-4 node-header"
          style={{ backgroundColor: '#ECFFE5' }}
        >
          <div className="d-flex mr-auto ml-2">
            <dt
              className="text-truncate fw-semibold fs-6"
              style={{ color: '#218500' }}
            >
              Extract Variable Node
            </dt>
          </div>
          <div className="ms-auto">
            <Button
              variant="transparent"
              onClick={e => trashNode(e, id)}
              className="d-flex align-items-center rounded-circle p-2 header-delete-icon"
              title="Delete extract_variable"
              disabled={!editable}
            >
              <UilTrashAlt
                style={{ width: '24px', height: '24px', color: '#dc3545' }}
              />
            </Button>
          </div>
        </div>

        <div className="card-body p-2">
          {/* Variables Section */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <dt className="text-truncate fw-medium mb-0 label-text">
                Variables
              </dt>
              <Button
                variant="transparent"
                onClick={addVariable}
                className="btn btn-sm btn-link p-0"
                disabled={!editable}
                title="Add Variable"
              >
                <UilPlus
                  style={{ width: '20px', height: '20px', color: '#6B7280' }}
                />
              </Button>
            </div>

            {/* Variable Fields */}
            {variables.map((variable, index) => (
              <div
                key={variable.id}
                className="d-flex align-items-center gap-4 position-relative fw-semibold mb-2"
              >
                <div
                  className="position-relative flex-fill"
                  style={{ cursor: 'pointer' }}
                  onClick={() => editable && openVariableModal(variable)}
                >
                  <SimpleLabelTextBox
                    dataProperty={variable.name}
                    editable={false}
                    placeholder={`Variable ${index + 1}`}
                    inputStyles={{
                      backgroundColor: '#f1f3f5',
                      cursor: 'pointer',
                      paddingRight: '35px'
                    }}
                    inputPropStyle={{ padding: '10px' }}
                    styles={{ flex: 1 }}
                  />
                  <UilPen
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px',
                      height: '16px',
                      color: '#6B7280',
                      pointerEvents: 'none'
                    }}
                  />
                </div>
                {variables.length > 1 && (
                  <Button
                    variant="transparent"
                    onClick={() => removeVariable(variable.id)}
                    className="btn btn-sm btn-link text-danger ms-2 p-0 ml-1"
                    disabled={!editable}
                    title="Remove variable"
                  >
                    <UilTrashAlt
                      style={{
                        width: '20px',
                        height: '20px',
                        color: '#dc3545'
                      }}
                    />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Space above transitions
          <div className="mb-2"></div> */}

          {/* Transitions Section - Like ChatTransfer */}
          <div className="px-4 pb-3">
            <dt className="text-truncate small fw-medium mt-3 px-4 label-text">
              Transition
            </dt>

            {transitions.map(item => (
              <div
                key={item.id}
                className="mt-1 d-flex align-items-center gap-4 position-relative fw-semibold mb-2"
              >
                {/* Source handle */}
                <FlowHandle
                  type="source"
                  position={Position.Right}
                  id={item.id}
                  style={{
                    right: '-13px',
                    top: '50%'
                  }}
                  editable={false}
                  isConnectable={true}
                  isValidConnection={IsValidConnection}
                />

                {/* Read-only label */}
                <SimpleLabelTextBox
                  dataProperty={item.value}
                  editable={false}
                  inputStyles={{
                    backgroundColor: '#f1f3f5',
                    cursor: 'not-allowed'
                  }}
                  inputPropStyle={{ padding: '10px' }}
                  styles={{ flex: 1 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Variable Edit Modal */}
      <ModalPopup
        open={showModal}
        handleClose={closeVariableModal}
        title="Edit Variable"
        onSave={modalFormik.handleSubmit}
        saveText="Save"
        cancelText="Cancel"
      >
        <div>
          <SimpleLabelTextBox
            key={`name-${selectedVariable?.id || 'new'}`}
            heading="Variable Name"
            field="name"
            dataProperty={modalFormik.values['name']}
            error={modalFormik.touched.name && modalFormik.errors.name}
            editable={true}
            placeholder="Enter variable name"
            handleChange={modalFormik.handleChange}
            onkeyPress={handleClipboardKeyDown}
            styles={{
              width: '100%',
              marginBottom: '1.5rem'
            }}
          />
          <DropDownEditable
            key={`type-${selectedVariable?.id || 'new'}`}
            heading="Variable Type"
            field="type"
            dataProperty={modalFormik.values['type']}
            error={modalFormik.touched.type && modalFormik.errors.type}
            editable={true}
            options={variableTypeOptions}
            handleChange={modalFormik.handleChange}
            styles={{
              width: '100%',
              marginBottom: '1.5rem'
            }}
          />
          <SimpleLabelTextBox
            key={`description-${selectedVariable?.id || 'new'}`}
            heading="Description"
            field="description"
            dataProperty={modalFormik.values['description']}
            editable={true}
            placeholder="Enter variable description"
            multiline
            maxRows={4}
            minRows={2}
            handleChange={modalFormik.handleChange}
            handleBlur={modalFormik.handleBlur}
            onkeyPress={handleClipboardKeyDown}
            error={
              modalFormik.touched.description && modalFormik.errors.description
            }
          />
        </div>
      </ModalPopup>
    </dl>
  );
}
